import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../styles/designTokens";

/* ============================================================
 * KEYFRAMES
 * ============================================================ */
const meshDrift = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-20px, 18px) scale(1.05); }
  100% { transform: translate(0, 0) scale(1); }
`;

const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50%      { transform: scale(1.15); opacity: 0.5; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const spinKf = keyframes`
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

/* ============================================================
 * MIXINS
 * ============================================================ */
const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum" 1;
`;

const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;

const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusLg};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut},
              transform ${tokens.durBase} ${tokens.easeOut},
              box-shadow ${tokens.durBase} ${tokens.easeOut};
`;

/* ============================================================
 * CONTAINER PRINCIPAL — com mesh de fundo + scroll interno
 * ============================================================ */
export const Container = styled.div`
  width: 100%;
  max-width: 90em;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 2em 2em 2em;
  position: relative;
  animation: ${fadeInUp} ${tokens.durSlow} ${tokens.easeOut};

  /* Mesh background — 2 blobs sutis, fixed, atrás de tudo */
  &::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(800px 600px at 15% 10%, ${tokens.brandSoft} 0%, transparent 60%),
      radial-gradient(700px 500px at 85% 70%, rgba(6, 182, 212, 0.05) 0%, transparent 65%);
    animation: ${meshDrift} 24s ${tokens.easeInOut} infinite;
    mask-image: radial-gradient(ellipse at center, black 40%, transparent 90%);
  }

  /* Tudo daqui pra baixo fica acima do mesh */
  > * { position: relative; z-index: 1; }

  /* ============================================================
   * HEADER — eyebrow + h1 + status dot
   * ============================================================ */
  .header-info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 0 8px 0;
    margin-bottom: 16px;

    .header-icon {
      display: none; /* ícone genérico removido */
    }

    .header-details {
      flex: 1;
      min-width: 0;

      h3 {
        margin: 4px 0 8px 0;
        font-size: 1.7em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.025em;
        line-height: 1.15;
      }

      /* Eyebrow sobre o título */
      h3::before {
        content: "UPLOAD DE DOCUMENTOS · PROJETO ATIVO";
        display: block;
        ${eyebrow};
        margin-bottom: 6px;
        opacity: 0.4;
      }

      .header-meta {
        display: flex;
        gap: 10px;
        align-items: center;
        font-size: 0.78em;

        span {
          padding: 0;
          background: transparent;
          border-radius: 0;
          font-weight: 400;
          opacity: 1;
        }

        /* Sistema Online vira dot pulsante + texto mono */
        .status-tag.active {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: ${tokens.brand};
          ${mono};
          font-size: 0.95em;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;

          &::before {
            content: "";
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: ${tokens.brand};
            box-shadow: 0 0 8px ${tokens.brandGlow};
            animation: ${pulseDot} 2.2s ${tokens.easeInOut} infinite;
          }
        }

        .update-tag {
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          ${mono};
          font-size: 0.9em;
          
          &::before {
            content: "· ";
            opacity: 0.5;
          }
        }
      }
    }
  }

  /* ============================================================
   * ACTION BUTTONS — CTA + ghost + admin pill
   * ============================================================ */
  .action-buttons {
    display: flex;
    gap: 10px;
    margin-bottom: 28px;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;

    .action-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 9px 16px;
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      background: ${({ theme }) => surface(theme, 0)};
      color: ${({ theme }) => theme.text};
      font-family: ${tokens.fontSans};
      font-size: 0.82em;
      font-weight: 500;
      cursor: pointer;
      letter-spacing: -0.005em;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      &:hover {
        background: ${({ theme }) => surface(theme, 1)};
        border-color: ${({ theme }) => hairline(theme, true)};
      }

      &.primary {
        background: ${tokens.brand};
        border-color: ${tokens.brand};
        color: #fff;

        &:hover:not(:disabled) {
          background: ${tokens.brandHover};
          border-color: ${tokens.brandHover};
          transform: translateY(-1px);
        }

        &:active {
          transform: translateY(0);
        }
      }

      &.danger {
        color: ${tokens.danger};
        border-color: rgba(239, 68, 68, 0.22);

        &:hover {
          background: ${tokens.dangerSoft};
          border-color: rgba(239, 68, 68, 0.4);
        }
      }

      &.info {
        color: ${tokens.info};
        border-color: rgba(59, 130, 246, 0.22);

        &:hover {
          background: ${tokens.infoSoft};
          border-color: rgba(59, 130, 246, 0.4);
        }
      }

      &.secondary {
        opacity: 0.7;
        &:hover { opacity: 1; }
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none !important;
      }

      svg {
        font-size: 1em;
        flex-shrink: 0;
      }

      .spin {
        animation: ${spinKf} 1s linear infinite;
      }
    }

    @media (max-width: 600px) {
      .action-btn {
        flex: 1;
        min-width: 140px;
        justify-content: center;
      }
    }
  }

  /* ============================================================
   * KPI CARDS — hierarquia hero (linha 1) + context (linha 2)
   * ============================================================ */
  .kpi-cards {
    display: grid;
    gap: 12px;
    margin-bottom: 14px;
    width: 100%;

    &.main-stats {
      /* 5 hero cards visualmente grandes */
      grid-template-columns: repeat(5, 1fr);
      gap: 14px;
    }

    &.secondary-stats {
      /* 5 context-strip cards compactos */
      grid-template-columns: repeat(5, 1fr);
      gap: 10px;
      margin-bottom: 28px;
    }

    @media (max-width: 1100px) {
      &.main-stats     { grid-template-columns: repeat(3, 1fr); }
      &.secondary-stats { grid-template-columns: repeat(3, 1fr); }
    }
    @media (max-width: 700px) {
      &.main-stats     { grid-template-columns: repeat(2, 1fr); }
      &.secondary-stats { grid-template-columns: repeat(2, 1fr); }
    }
  }

  .kpi-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px 18px 14px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 96px;
    position: relative;
    overflow: hidden;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }

    /* Cards "concluídos" (100%) — thumb em esmeralda sutil */
    &.is-complete {
      .card-content .thumb { color: ${tokens.brand}; opacity: 0.7; }
    }

    .card-content {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .thumb {
        opacity: 0.22;
        font-size: 0.9em;
        color: ${({ theme }) => theme.text};
        display: inline-flex;
        align-items: center;
      }

      .text {
        display: flex;
        flex-direction: column;
        gap: 2px;

        p {
          margin: 0;
        }

        /* Número principal — sutil, não hero */
        p:first-child {
          ${mono};
          font-size: 1.35em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.02em;
          line-height: 1.1;
        }

        /* Label */
        p:last-child {
          font-size: 0.68em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          margin-top: 2px;
          letter-spacing: -0.005em;
        }
      }
    }

    /* Cards compactos (linha 2): hero menor + label menor */
    &.compact {
      padding: 12px 14px;
      min-height: 68px;

      .card-content {
        gap: 2px;

        .thumb { display: none; }

        .text p:first-child {
          font-size: 1.05em;
          font-weight: 500;
        }
        .text p:last-child {
          font-size: 0.62em;
          margin-top: 2px;
          opacity: 0.5;
        }
      }
    }

    /* Estado warning (XMLs Faltantes > 0) — número em âmbar sutil */
    &.rejected .card-content .text p:first-child {
      color: ${({ theme, $hasMissing }) => $hasMissing ? tokens.warn : theme.text};
      opacity: 0.7;
    }

    /* Progress bar — 2px discreta, sem shimmer */
    &.with-progress {
      .kpi-progress {
        height: 2px;
        background: ${({ theme }) => surface(theme, 2)};
        border-radius: ${tokens.radiusPill};
        margin-top: 12px;
        overflow: hidden;
        position: relative;

        .kpi-progress-fill {
          height: 100%;
          border-radius: ${tokens.radiusPill};
          background: ${tokens.brand};
          transition: width 0.6s ${tokens.easeOut};
        }
      }
    }
  }

  /* ============================================================
   * TABS — segmented control estilo Vercel
   * ============================================================ */
  .files-tabs {
    display: inline-flex;
    gap: 4px;
    padding: 4px;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    margin-bottom: 20px;
    width: fit-content;

    button {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      border-radius: ${tokens.radiusSm};
      border: none;
      background: transparent;
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      font-family: ${tokens.fontSans};
      font-size: 0.82em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      letter-spacing: -0.005em;

      &:hover {
        opacity: 0.85;
        background: ${({ theme }) => surface(theme, 1)};
      }

      &.active {
        opacity: 1;
        background: ${({ theme }) => surface(theme, 2)};
        color: ${tokens.brand};
        box-shadow: inset 0 0 0 1px ${({ theme }) => hairline(theme, true)};
      }

      svg { font-size: 0.95em; opacity: 0.85; }
    }
  }

  /* ============================================================
   * EMPRESAS GRID — 4 colunas, mesma largura total dos 5 KPIs
   * ============================================================ */
  .empresas-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 14px;
    margin-bottom: 32px;
    width: 100%;

    @media (max-width: 1100px) { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    @media (max-width: 700px)  { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }

  /* ============================================================
   * EMPRESA CARD — versão compacta com heatmap mini (4 por linha)
   * ============================================================ */
  .empresa-card-new {
    ${({ theme }) => cardBase(theme)};
    padding: 14px 16px;
    cursor: pointer;
    font-family: ${tokens.fontSans};
    display: flex;
    flex-direction: column;
    gap: 12px;

    &:hover {
      transform: translateY(-1px);
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};

      .empresa-icon {
        background: ${tokens.brand};
        color: #fff;

        .icon-wrapper.default { opacity: 0 !important; transform: scale(0.6) !important; }
        .icon-wrapper.hover   { opacity: 1 !important; transform: scale(1) !important; }
      }

      .heatmap-mini-cell.has-data {
        opacity: 1;
      }
    }

    &.selected {
      border-color: ${tokens.brandRing};
    }

    /* --- Header --- */
    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 10px;
      padding-bottom: 10px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};

      .empresa-info {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;

        .empresa-nome {
          font-size: 0.85em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.01em;
          line-height: 1.25;
          text-transform: capitalize;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .empresa-cnpj {
          ${mono};
          font-size: 0.62em;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
        }
      }

      .empresa-icon {
        width: 30px;
        height: 30px;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${({ theme }) => surface(theme, 1)};
        border: 1px solid ${({ theme }) => hairline(theme)};
        border-radius: ${tokens.radiusSm};
        color: ${tokens.brand};
        font-size: 0.85em;
        transition: all ${tokens.durBase} ${tokens.easeOut};
        position: relative;
        overflow: hidden;
        cursor: pointer;

        .icon-wrapper {
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all ${tokens.durBase} ${tokens.easeOut};
          svg { font-size: 1em; }
        }
        .icon-wrapper.default { opacity: 1; transform: scale(1); }
        .icon-wrapper.hover   { opacity: 0; transform: scale(0.6); }
      }
    }

    /* --- Período em uma linha --- */
    .periodo-line {
      display: flex;
      align-items: center;
      gap: 8px;
      ${mono};
      font-size: 0.7em;
      color: ${({ theme }) => theme.text};
      opacity: 0.55;

      .periodo-arrow {
        opacity: 0.4;
      }
    }

    /* --- 3 micro-stats verticais --- */
    .micro-stats {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .micro-stat {
        display: flex;
        flex-direction: column;
        gap: 3px;

        .micro-stat-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .micro-stat-label {
          ${eyebrow};
          font-size: 0.6em;
          opacity: 0.45;
        }

        .micro-stat-value {
          ${mono};
          font-size: 0.78em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.85;

          &.complete {
            color: ${tokens.brand};
            opacity: 1;
          }

          &.warning {
            opacity: 0.7; color: ${tokens.warn};
            opacity: 1;
          }
        }

        .micro-progress {
          height: 2px;
          background: ${({ theme }) => surface(theme, 2)};
          border-radius: ${tokens.radiusPill};
          overflow: hidden;

          .micro-progress-fill {
            height: 100%;
            border-radius: ${tokens.radiusPill};
            background: ${tokens.brand};
            transition: width 0.5s ${tokens.easeOut};
          }
        }
      }
    }

    /* --- Heatmap mini (1 linha, 12 cells do ano corrente) --- */
    .heatmap-mini {
      display: flex;
      flex-direction: column;
      gap: 5px;
      padding-top: 8px;
      border-top: 1px solid ${({ theme }) => hairline(theme)};

      .heatmap-mini-header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;

        .heatmap-mini-label {
          ${eyebrow};
          font-size: 0.58em;
          opacity: 0.4;
        }

        .heatmap-mini-year {
          ${mono};
          font-size: 0.6em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.55;
        }
      }

      .heatmap-mini-grid {
        display: grid;
        grid-template-columns: repeat(12, 1fr);
        gap: 2px;
      }

      .heatmap-mini-cell {
        aspect-ratio: 1;
        border-radius: 2px;
        background: ${({ theme }) => surface(theme, 2)};

        &.has-data {
          background: ${tokens.brand};
          opacity: 0.35;
          transition: opacity ${tokens.durBase} ${tokens.easeOut};
        }
      }
    }

    /* --- Cert pill mini --- */
    .cert-pill {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 8px;
      border-radius: ${tokens.radiusPill};
      font-size: 0.62em;
      ${mono};
      font-weight: 500;
      align-self: flex-start;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      &.presente {
        background: ${tokens.brandSoft};
        color: ${tokens.brand};
        border: 1px solid ${tokens.brandRing};
      }

      &.ausente {
        background: ${({ theme }) => surface(theme, 1)};
        color: ${({ theme }) => theme.text};
        opacity: 0.45;
        border: 1px solid ${({ theme }) => hairline(theme)};
      }

      svg { font-size: 1em; }
    }
  }


  /* ============================================================
   * ARQUIVO CARD (lista de uploads)
   * ============================================================ */
  .arquivo-card {
    ${({ theme }) => cardBase(theme)};
    padding: 14px 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    overflow: hidden;
    gap: 14px;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }

    .download-progress-bar {
      position: absolute;
      bottom: 0;
      left: 0;
      height: 2px;
      background: ${tokens.brand};
      transition: width 0.3s ${tokens.easeOut};
      z-index: 10;
    }

    .arquivo-icon {
      width: 40px;
      height: 40px;
      flex-shrink: 0;
      border-radius: ${tokens.radiusMd};
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${tokens.brand};
      font-size: 18px;
    }

    .arquivo-info {
      flex: 1;
      min-width: 0;

      .arquivo-nome {
        color: ${({ theme }) => theme.text};
        font-weight: 500;
        font-size: 0.9em;
        margin-bottom: 4px;
        letter-spacing: -0.005em;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .arquivo-meta {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 0.68em;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
        ${mono};

        .separator {
          opacity: 0.4;
        }
      }
    }

    .arquivo-actions {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;

      .status-badge {
        padding: 4px 10px;
        border-radius: ${tokens.radiusPill};
        font-size: 0.7em;
        font-weight: 500;
        ${mono};
        text-transform: uppercase;
        letter-spacing: 0.04em;
        border: 1px solid transparent;

        &.uploaded {
          background: ${tokens.infoSoft};
          color: ${tokens.info};
          border-color: rgba(59, 130, 246, 0.18);
        }

        &.processed {
          background: ${tokens.brandSoft};
          color: ${tokens.brand};
          border-color: ${tokens.brandRing};
        }

        &.error {
          background: ${tokens.dangerSoft};
          color: ${tokens.danger};
          border-color: rgba(239, 68, 68, 0.2);
        }
      }

      .action-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: ${tokens.radiusSm};
        background: ${({ theme }) => surface(theme, 1)};
        border: 1px solid ${({ theme }) => hairline(theme)};
        cursor: pointer;
        transition: all ${tokens.durBase} ${tokens.easeOut};
        text-decoration: none;

        &.download { color: ${tokens.brand}; }
        &.delete   { color: ${tokens.danger}; }

        &:hover {
          background: ${({ theme }) => surface(theme, 2)};
          border-color: ${({ theme }) => hairline(theme, true)};
        }
      }

      .loading-spinner {
        width: 18px;
        height: 18px;
        border: 2px solid ${tokens.brandSoft};
        border-left-color: ${tokens.brand};
        border-radius: 50%;
        animation: ${spinKf} 0.9s linear infinite;
      }
    }
  }

  /* ============================================================
   * EMPTY STATE (Arquivos Enviados vazio)
   * ============================================================ */
  .empty-state {
    text-align: center;
    padding: 56px 24px;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px dashed ${({ theme }) => hairline(theme, true)};
    border-radius: ${tokens.radiusLg};

    .empty-icon {
      font-size: 40px;
      color: ${({ theme }) => theme.text};
      opacity: 0.25;
      margin-bottom: 12px;
    }

    .empty-title {
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      font-size: 0.95em;
      font-weight: 500;
      margin: 0;
    }

    .empty-hint {
      color: ${({ theme }) => theme.text};
      opacity: 0.4;
      font-size: 0.8em;
      margin-top: 6px;
      ${mono};
    }
  }

  @keyframes spin {
    0%   { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

/* ============================================================
 * MODAL SYSTEM (Download / Reset)
 * ============================================================ */
export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(8, 9, 12, 0.7);
  backdrop-filter: ${tokens.blurBg};
  -webkit-backdrop-filter: ${tokens.blurBg};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${fadeIn} ${tokens.durBase} ${tokens.easeOut};
`;

export const ModalContainer = styled.div`
  background: ${({ theme }) => theme.card};
  border-radius: ${tokens.radiusLg};
  width: 560px;
  max-width: 92%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: ${tokens.shadowLg};
  border: 1px solid ${({ theme }) => hairline(theme, true)};
  animation: ${fadeIn} ${tokens.durSlow} ${tokens.easeOut};
  overflow: hidden;
  position: relative;

  /* Top hairline gradient */
  &::before {
    content: "";
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${tokens.brandRing}, transparent);
  }
`;

export const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  display: flex;
  justify-content: space-between;
  align-items: center;

  h3 {
    font-size: 1.05em;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    margin: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    letter-spacing: -0.015em;
  }
`;

export const ModalBody = styled.div`
  padding: 22px 24px;
  overflow-y: auto;
  display: grid;
  gap: 12px;
`;

export const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  background: ${({ theme }) => surface(theme, 0)};
`;

export const DownloadIconWrapper = styled.div`
  width: 44px;
  height: 44px;
  border-radius: ${tokens.radiusMd};
  background: ${({ bgColor, theme }) => bgColor || surface(theme, 1)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all ${tokens.durBase} ${tokens.easeOut};

  svg {
    font-size: 20px;
    color: ${({ iconColor, theme }) => iconColor || tokens.brand};
  }
`;

export const DownloadCard = styled.div`
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  transition: all ${tokens.durBase} ${tokens.easeOut};
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &:hover {
    background: ${({ theme }) => surface(theme, 1)};
    border-color: ${({ theme }) => hairline(theme, true)};

    ${DownloadIconWrapper} {
      background: ${({ theme }) => surface(theme, 2)};
      border-color: ${({ theme }) => hairline(theme, true)};
    }

    /* chevron desliza */
    > svg:last-child {
      transform: translateX(4px);
      opacity: 1 !important;
    }
  }

  &:active {
    transform: translateX(1px);
  }

  > svg:last-child {
    transition: transform ${tokens.durBase} ${tokens.easeOut},
                opacity ${tokens.durBase} ${tokens.easeOut};
  }
`;

export const ActionButton = styled.button`
  padding: 9px 18px;
  border-radius: ${tokens.radiusMd};
  font-weight: 500;
  font-size: 0.85em;
  font-family: ${tokens.fontSans};
  cursor: pointer;
  border: 1px solid
    ${({ primary, theme }) =>
      primary ? tokens.brand : hairline(theme)};
  background: ${({ primary, theme }) =>
    primary ? tokens.brand : surface(theme, 0)};
  color: ${({ primary, theme }) => (primary ? "#fff" : theme.text)};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all ${tokens.durBase} ${tokens.easeOut};

  &:hover:not(:disabled) {
    ${({ primary }) => primary && `transform: translateY(-1px);`}
    background: ${({ primary, theme }) =>
      primary ? tokens.brandHover : surface(theme, 1)};
    border-color: ${({ primary, theme }) =>
      primary ? tokens.brandHover : hairline(theme, true)};
  }

  &:active { transform: translateY(0); }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/* ============================================================
 * EMPRESA DETAILS MODAL — compacto e sofisticado
 * ============================================================ */
export const EmpresaModalContainer = styled.div`
  background: ${({ theme }) => theme.card};
  border-radius: ${tokens.radiusLg};
  width: 640px;
  max-width: 94%;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  box-shadow: ${tokens.shadowLg};
  border: 1px solid ${({ theme }) => hairline(theme, true)};
  animation: ${fadeIn} ${tokens.durSlow} ${tokens.easeOut};
  overflow: hidden;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${tokens.brandRing}, transparent);
  }

  /* ============ HEADER ============ */
  .empresa-modal-header {
    padding: 18px 22px 14px;
    border-bottom: 1px solid ${({ theme }) => hairline(theme)};
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;

    .empresa-modal-title {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;

      .empresa-eyebrow {
        ${mono};
        font-size: 0.58em;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: ${tokens.brand};
        opacity: 0.65;
      }

      h2 {
        margin: 0;
        font-size: 0.98em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.015em;
        text-transform: capitalize;
        line-height: 1.2;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .empresa-modal-cnpj {
        ${mono};
        font-size: 0.7em;
        color: ${({ theme }) => theme.text};
        opacity: 0.45;
        margin-top: 1px;
      }
    }

    .empresa-modal-close {
      width: 28px;
      height: 28px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: ${tokens.radiusSm};
      background: transparent;
      color: ${({ theme }) => theme.text};
      opacity: 0.4;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      font-size: 0.95em;

      &:hover {
        opacity: 1;
        background: ${({ theme }) => surface(theme, 1)};
      }
    }
  }

  /* ============ BODY ============ */
  .empresa-modal-body {
    padding: 16px 22px 20px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  /* Section header (eyebrow + content) */
  .section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .section-eyebrow {
    ${mono};
    font-size: 0.6em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: ${({ theme }) => theme.text};
    opacity: 0.22;
    display: flex;
    align-items: center;
    gap: 8px;

    /* hairline lateral pra dar leveza */
    &::after {
      content: "";
      flex: 1;
      height: 1px;
      background: ${({ theme }) => hairline(theme)};
      opacity: 0.5;
    }
  }

  /* ============ PERÍODO inline ============ */
  .periodo-inline {
    display: flex;
    align-items: center;
    gap: 12px;
    ${mono};
    font-size: 0.86em;
    color: ${({ theme }) => theme.text};
    letter-spacing: -0.01em;

    .periodo-value {
      font-weight: 500;
    }

    .periodo-arrow {
      opacity: 0.22;
      font-size: 0.9em;
    }

    .periodo-tag {
      ${eyebrow};
      font-size: 0.62em;
      opacity: 0.4;
      margin-right: 2px;
    }
  }

  /* ============ STATS principais (XMLs/NFe/CTe) ============ */
  .stats-section {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .stat-row {
      display: grid;
      grid-template-columns: 64px 1fr auto auto;
      align-items: center;
      gap: 12px;

      .stat-label {
        font-size: 0.78em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.55;
        letter-spacing: -0.005em;
      }

      .stat-progress {
        height: 2px;
        background: ${({ theme }) => surface(theme, 2)};
        border-radius: ${tokens.radiusPill};
        overflow: hidden;

        .stat-progress-fill {
          height: 100%;
          border-radius: ${tokens.radiusPill};
          background: ${tokens.brand};
          transition: width 0.5s ${tokens.easeOut};
        }
      }

      .stat-percent {
        ${mono};
        font-size: 0.7em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.45;
        min-width: 38px;
        text-align: right;

        &.complete {
          color: ${tokens.brand};
          opacity: 1;
        }
      }

      .stat-value {
        ${mono};
        font-size: 0.78em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        text-align: right;
        min-width: 96px;

        &.complete { color: ${tokens.brand}; }
        &.warning  { opacity: 0.7; color: ${tokens.warn}; }
      }
    }
  }

  /* ============ PAIRS — formato tabela com dividers sutis ============ */
  .pairs-list {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    overflow: hidden;

    .pair-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 14px;
      background: ${({ theme }) => surface(theme, 0)};

      &:nth-child(odd) {
        border-right: 1px solid ${({ theme }) => hairline(theme)};
      }
      &:nth-child(n+3) {
        border-top: 1px solid ${({ theme }) => hairline(theme)};
      }

      .pair-label {
        font-size: 0.74em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.55;
      }

      .pair-value {
        ${mono};
        font-size: 0.76em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.85;

        &.complete {
          color: ${tokens.brand};
          opacity: 1;

          &::before {
            content: "";
            display: inline-block;
            width: 5px;
            height: 5px;
            border-radius: 50%;
            background: ${tokens.brand};
            margin-right: 6px;
            vertical-align: middle;
            box-shadow: 0 0 6px ${tokens.brandGlow};
          }
        }
      }
    }
  }

  /* ============ Faltantes inline (não bloco) ============ */
  .faltantes-inline {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    border-left: 2px solid ${tokens.warn};
    background: linear-gradient(90deg, ${tokens.warnSoft}, transparent);
    border-radius: 0 ${tokens.radiusSm} ${tokens.radiusSm} 0;

    .faltantes-label {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 0.78em;
      font-weight: 500;
      opacity: 0.7; color: ${tokens.warn};

      svg { font-size: 0.95em; }
    }

    .faltantes-value {
      ${mono};
      font-size: 0.85em;
      font-weight: 600;
      opacity: 0.7; color: ${tokens.warn};
    }
  }

  /* ============ Certificado — linha discreta ============ */
  .cert-inline {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 0;
    font-size: 0.78em;

    .cert-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    &.presente {
      color: ${tokens.brand};
      .cert-dot {
        background: ${tokens.brand};
        box-shadow: 0 0 8px ${tokens.brandGlow};
      }
    }

    &.ausente {
      color: ${({ theme }) => theme.text};
      opacity: 0.45;
      .cert-dot {
        background: ${({ theme }) => theme.text};
        opacity: 0.4;
      }
    }

    span {
      font-weight: 500;
      letter-spacing: -0.005em;
    }
  }

  /* ============ Histórico mensal compacto ============ */
  .historico-grid {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .historico-row {
      display: grid;
      grid-template-columns: 36px repeat(12, 1fr);
      gap: 3px;
      align-items: center;

      .historico-year {
        ${mono};
        font-size: 0.62em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.45;
        letter-spacing: 0.02em;
      }
    }

    /* Months label row (no topo) */
    .historico-months-label {
      display: grid;
      grid-template-columns: 36px repeat(12, 1fr);
      gap: 3px;
      margin-bottom: 2px;

      span:first-child { }
      span:not(:first-child) {
        ${mono};
        font-size: 0.52em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.3;
        text-align: center;
        letter-spacing: 0.05em;
      }
    }

    .historico-cell {
      aspect-ratio: 1;
      border-radius: 2px;
      background: ${({ theme }) => surface(theme, 2)};
      position: relative;
      transition: transform ${tokens.durFast} ${tokens.easeOut};

      &.has-data { background: transparent; cursor: pointer; }

      .cell-content {
        position: absolute;
        inset: 0;
        border-radius: 2px;
        background: ${tokens.brand};
        transition: transform ${tokens.durFast} ${tokens.easeOut};
      }

      &:hover {
        z-index: 10;
        transform: scale(1.25);
      }

      /* Tooltip */
      .historico-tooltip {
        display: none;
        position: absolute;
        bottom: calc(100% + 8px);
        left: 50%;
        transform: translateX(-50%);
        z-index: 1000;
        background: ${({ theme }) => theme.body === "#fafbfc" ? "#ffffff" : "#15171c"};
        border: 1px solid ${({ theme }) => hairline(theme, true)};
        border-radius: ${tokens.radiusMd};
        padding: 10px 12px;
        min-width: 180px;
        box-shadow: ${tokens.shadowLg};
        pointer-events: none;
        text-align: left;

        .tt-title {
          ${mono};
          font-size: 0.62em;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: ${tokens.brand};
          opacity: 0.7;
          margin-bottom: 8px;
          padding-bottom: 6px;
          border-bottom: 1px solid ${({ theme }) => hairline(theme)};
          text-align: center;
        }

        .tt-row {
          display: flex;
          justify-content: space-between;
          font-size: 0.7em;
          margin-top: 4px;

          &.warning {
            margin-top: 6px;
            padding-top: 6px;
            border-top: 1px solid ${({ theme }) => hairline(theme)};
            opacity: 0.7; color: ${tokens.warn};
            font-weight: 600;
          }

          span:first-child {
            color: ${({ theme }) => theme.text};
            opacity: 0.55;
          }
          span:last-child {
            ${mono};
            font-weight: 500;
            color: ${({ theme }) => theme.text};
          }
        }
      }

      &:hover .historico-tooltip { display: block; }
    }
  }

  /* ============ FOOTER ============ */
  .empresa-modal-footer {
    padding: 12px 22px;
    border-top: 1px solid ${({ theme }) => hairline(theme)};
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    background: ${({ theme }) => surface(theme, 0)};
  }
`;

